import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { errorMessage } from './errors'
import { http } from './api'
import type { OAuthClientListItem } from './oauth-types'
import type {
  AccountEmailInput,
  BrandTemplate,
  ContractHoursRow,
  CreateContractHoursInput,
  CreatePersonInput,
  CreatePersonRateInput,
  PersonAccess,
  PersonRateRow,
  PersonRow,
  UpdateAccessInput,
  SystemSetting,
  SystemSettingsChanges,
  UpdatePersonInput,
  UpdatePersonRateInput,
  UpdateTemplateInput,
} from './settings-types'

export interface SettingsApi {
  personOAuthClients(personId: string): Promise<OAuthClientListItem[]>
  revokePersonOAuthClient(personId: string, clientId: string): Promise<void>
  reactivatePersonOAuthClient(personId: string, clientId: string): Promise<OAuthClientListItem>
  persons(): Promise<PersonRow[]>
  createPerson(input: CreatePersonInput): Promise<PersonRow>
  updatePerson(id: string, input: UpdatePersonInput): Promise<PersonRow>
  registerAccount(personId: string, input: AccountEmailInput): Promise<PersonRow>
  updateAccountEmail(personId: string, input: AccountEmailInput): Promise<PersonRow>
  getAccess(personId: string): Promise<PersonAccess>
  updateAccess(personId: string, input: UpdateAccessInput): Promise<PersonAccess>
  createContractHours(personId: string, input: CreateContractHoursInput): Promise<ContractHoursRow>
  deleteContractHours(personId: string, contractId: string): Promise<void>
  personRates(): Promise<PersonRateRow[]>
  createPersonRate(input: CreatePersonRateInput): Promise<PersonRateRow>
  updatePersonRate(id: string, input: UpdatePersonRateInput): Promise<PersonRateRow>
  templates(): Promise<BrandTemplate[]>
  updateTemplate(code: string, input: UpdateTemplateInput): Promise<BrandTemplate>
  systemSettings(): Promise<SystemSetting[]>
  updateSystemSettings(changes: SystemSettingsChanges): Promise<SystemSetting[]>
}

class RealSettingsApi implements SettingsApi {
  async personOAuthClients(personId: string) {
    const res = await http.get<{ clients: OAuthClientListItem[] }>(`/api/persons/${personId}/account/oauth-clients`)
    return res.clients
  }
  revokePersonOAuthClient(personId: string, clientId: string) {
    return http.del<void>(`/api/persons/${personId}/account/oauth-clients/${clientId}`)
  }
  reactivatePersonOAuthClient(personId: string, clientId: string) {
    return http.post<OAuthClientListItem>(`/api/persons/${personId}/account/oauth-clients/${clientId}/reactivate`)
  }
  async persons() {
    const res = await http.get<{ persons: PersonRow[] }>('/api/persons')
    return res.persons
  }
  createPerson(input: CreatePersonInput) {
    return http.post<PersonRow>('/api/persons', input)
  }
  updatePerson(id: string, input: UpdatePersonInput) {
    return http.patch<PersonRow>(`/api/persons/${id}`, input)
  }
  registerAccount(personId: string, input: AccountEmailInput) {
    return http.post<PersonRow>(`/api/persons/${personId}/account`, input)
  }
  updateAccountEmail(personId: string, input: AccountEmailInput) {
    return http.patch<PersonRow>(`/api/persons/${personId}/account`, input)
  }
  getAccess(personId: string) {
    return http.get<PersonAccess>(`/api/persons/${personId}/access`)
  }
  updateAccess(personId: string, input: UpdateAccessInput) {
    return http.put<PersonAccess>(`/api/persons/${personId}/access`, input)
  }
  createContractHours(personId: string, input: CreateContractHoursInput) {
    return http.post<ContractHoursRow>(`/api/persons/${personId}/contract-hours`, input)
  }
  deleteContractHours(personId: string, contractId: string) {
    return http.del<void>(`/api/persons/${personId}/contract-hours/${contractId}`)
  }
  async personRates() {
    const res = await http.get<{ personRates: PersonRateRow[] }>('/api/person-rates')
    return res.personRates
  }
  createPersonRate(input: CreatePersonRateInput) {
    return http.post<PersonRateRow>('/api/person-rates', input)
  }
  updatePersonRate(id: string, input: UpdatePersonRateInput) {
    return http.patch<PersonRateRow>(`/api/person-rates/${id}`, input)
  }
  templates() {
    return http.get<BrandTemplate[]>('/api/invoice-templates')
  }
  updateTemplate(code: string, input: UpdateTemplateInput) {
    return http.patch<BrandTemplate>(`/api/invoice-templates/${code}`, input)
  }
  async systemSettings() {
    const res = await http.get<{ settings: SystemSetting[] }>('/api/settings/system')
    return res.settings
  }
  async updateSystemSettings(changes: SystemSettingsChanges) {
    const res = await http.patch<{ settings: SystemSetting[] }>('/api/settings/system', changes)
    return res.settings
  }
}

export const settingsApi: SettingsApi = new RealSettingsApi()

export const settingsKeys = {
  personOAuthClients: (personId: string) => ['settings', 'person-oauth-clients', personId] as const,
  persons: ['settings', 'persons'] as const,
  personRates: ['settings', 'person-rates'] as const,
  system: ['settings', 'system'] as const,
  templates: ['settings', 'templates'] as const,
}

export function usePersonOAuthClients(personId: string, enabled: boolean) {
  return useQuery({ queryKey: settingsKeys.personOAuthClients(personId), queryFn: () => settingsApi.personOAuthClients(personId), enabled })
}

export function useRevokePersonOAuthClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { personId: string; clientId: string }) => settingsApi.revokePersonOAuthClient(vars.personId, vars.clientId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.personOAuthClients(vars.personId) })
      queryClient.invalidateQueries({ queryKey: settingsKeys.persons })
      toast.success('Koppeling uitgeschakeld')
    },
    onError: (e) => toast.error(errorMessage(e, 'Uitschakelen mislukt')),
  })
}

export function useReactivatePersonOAuthClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { personId: string; clientId: string }) => settingsApi.reactivatePersonOAuthClient(vars.personId, vars.clientId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.personOAuthClients(vars.personId) })
      queryClient.invalidateQueries({ queryKey: settingsKeys.persons })
      toast.success('Koppeling weer ingeschakeld')
    },
    onError: (e) => toast.error(errorMessage(e, 'Inschakelen mislukt')),
  })
}

export function useRegisterAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { personId: string; input: AccountEmailInput }) => settingsApi.registerAccount(vars.personId, vars.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.persons })
      toast.success('Account geregistreerd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Registreren mislukt')),
  })
}

export function useUpdateAccountEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { personId: string; input: AccountEmailInput }) => settingsApi.updateAccountEmail(vars.personId, vars.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.persons })
      toast.success('E-mailadres gewijzigd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Wijzigen mislukt')),
  })
}

export function useUpdateAccess() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { personId: string; input: UpdateAccessInput }) => settingsApi.updateAccess(vars.personId, vars.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.persons })
      toast.success('Toegang bijgewerkt')
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}

export function usePersons(enabled = true) {
  return useQuery({ queryKey: settingsKeys.persons, queryFn: () => settingsApi.persons(), enabled })
}

export function useSavePerson() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id?: string; create?: CreatePersonInput; update?: UpdatePersonInput }) =>
      vars.id ? settingsApi.updatePerson(vars.id, vars.update ?? {}) : settingsApi.createPerson(vars.create as CreatePersonInput),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.persons })
      toast.success(vars.id ? 'Persoon opgeslagen' : 'Persoon aangemaakt')
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}

export function useSaveContractHours() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { personId: string; input: CreateContractHoursInput }) =>
      settingsApi.createContractHours(vars.personId, vars.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.persons })
      toast.success('Contracturen toegevoegd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}

export function useDeleteContractHours() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { personId: string; contractId: string }) =>
      settingsApi.deleteContractHours(vars.personId, vars.contractId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.persons })
      toast.success('Contracturen-periode verwijderd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Verwijderen mislukt')),
  })
}

export function usePersonRates(enabled = true) {
  return useQuery({ queryKey: settingsKeys.personRates, queryFn: () => settingsApi.personRates(), enabled })
}

export function useSavePersonRate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id?: string; create?: CreatePersonRateInput; update?: UpdatePersonRateInput }) =>
      vars.id ? settingsApi.updatePersonRate(vars.id, vars.update ?? {}) : settingsApi.createPersonRate(vars.create as CreatePersonRateInput),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.personRates })
      toast.success(vars.id ? 'Tarief opgeslagen' : 'Tarief toegevoegd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}

export function useBrandTemplates() {
  return useQuery({ queryKey: settingsKeys.templates, queryFn: () => settingsApi.templates() })
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { code: string; input: UpdateTemplateInput }) => settingsApi.updateTemplate(vars.code, vars.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.templates })
      toast.success('Huisstijl opgeslagen')
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}

export function useSystemSettings(enabled: boolean) {
  return useQuery({ queryKey: settingsKeys.system, queryFn: () => settingsApi.systemSettings(), enabled })
}

export function useUpdateSystemSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (changes: SystemSettingsChanges) => settingsApi.updateSystemSettings(changes),
    onSuccess: (settings) => {
      queryClient.setQueryData(settingsKeys.system, settings)
      toast.success('Instellingen opgeslagen')
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}
